import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Button,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  DialogContentText,
  FormHelperText
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import TimeToLeaveIcon from "@mui/icons-material/TimeToLeave";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import MissionData from "../../mission/Timesheet/MissionData";
import MissionSummaryService from "../../../services/MissionSummaryService";
import LoadingCustomBackdrop from "../../tools/LoadingCustomBackdrop";
import Spacer from "../../../components/Spacer";
import ProvisionalFuelMissionService from "../../../services/ProvisionalFuelMissionService";
import MissionTransportService from "../../../services/MissionTransportService";
import TransportData from "../../mission/fuel/TransportData";
import RentalChargeTable from "../../mission/fuel/RentalChargeTable";
import { formatDate } from "../../../helpers/FormateDate";
import formatWithThousandSeparator from "../../../helpers/FormatWithThousandSeparator";
import { Dialog, makeStyles } from "@material-ui/core";

const LOCATION_FIELDS = {
  depart: {
    region: "region_depart",
    district: "district_depart",
    commune: "commune_depart",
    fokontany: "fokontany_depart",
  },
  arrivee: {
    region: "region_arrivee",
    district: "district_arrivee",
    commune: "commune_arrivee",
    fokontany: "fokontany_arrivee",
  },
};

const parseNumber = (value, fallback = 0) => {
  const parsed = parseFloat((value ?? "").toString().replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatPercent = (value) => {
  const numeric = parseNumber(value, 0);
  return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(2);
};

const getVehicleLabel = (transport) =>
  transport?.vhc_imm ||
  transport?.rent_type ||
  transport?.designation ||
  `Transport #${transport?.transport_id ?? "?"}`;

const getTransportType = (transport) =>
  transport?.vhc_type || transport?.type || "Non renseigne";

const getTransportMode = (transport) =>
  transport?.designation || transport?.rent_type || "Non renseigne";

const getLocationLabel = (entry, type) => {
  const field = LOCATION_FIELDS[type];
  const region = entry?.[field.region] || "-";
  const district = entry?.[field.district] || "-";
  const commune = entry?.[field.commune] || "-";
  const fokontany = entry?.[field.fokontany] || "-";
  return `${region} / ${district} / ${commune} / ${fokontany}`;
};

const buildFinancementKey = (financement, index = 0) => {
  if (!financement) {
    return `fin-${index}`;
  }
  const code = (financement?.code_financement_mis ?? "").toString().trim();
  const id = financement?.id_financement_mis;
  const hasId =
    id !== null && id !== undefined && `${id}` !== "" && !Number.isNaN(Number(id));
  if (hasId || code) {
    return `${hasId ? Number(id) : ""}|${code}`;
  }
  return `fin-${index}`;
};

const getTransportFinancements = (transport) =>
  Array.isArray(transport?.financements) ? transport.financements : [];

const normalizeFinancement = (financement, defaultPercent = 0) => ({
  id_financement_mis: Number.isFinite(Number(financement?.id_financement_mis))
    ? Number(financement.id_financement_mis)
    : 0,
  code_financement_mis:
    (financement?.code_financement_mis ?? "").toString().trim() ||
    "SANS_CODE_FINANCEMENT",
  percentage_rate: parseNumber(financement?.percentage_rate, defaultPercent),
  amount:
    financement?.amount !== undefined && financement?.amount !== null
      ? parseNumber(financement.amount, 0)
      : null,
});

const getFinancementAmount = (financement, baseAmount = 0) =>
  financement?.amount !== null && financement?.amount !== undefined
    ? parseNumber(financement.amount, 0)
    : (parseNumber(baseAmount, 0) *
        parseNumber(financement?.percentage_rate, 0)) /
      100;

const getDestinationFinancements = (destination, transport) => {
  const lineFinancements = Array.isArray(destination?.line_financements)
    ? destination.line_financements
    : [];
  if (lineFinancements.length) {
    const defaultPercent = 100 / lineFinancements.length;
    return lineFinancements.map((fin) => normalizeFinancement(fin, defaultPercent));
  }

  if (
    destination?.id_financement_mis !== undefined ||
    destination?.code_financement_mis !== undefined
  ) {
    return [
      normalizeFinancement(
        {
          id_financement_mis: destination?.id_financement_mis,
          code_financement_mis: destination?.code_financement_mis,
          percentage_rate: 100,
        },
        100
      ),
    ];
  }

  const transportFinancements = getTransportFinancements(transport);
  if (!transportFinancements.length) {
    return [];
  }
  const defaultPercent = 100 / transportFinancements.length;
  return transportFinancements.map((fin) =>
    normalizeFinancement(fin, defaultPercent)
  );
};

const getTransportFinancementColumns = (transport, rows) => {
  const mapByKey = new Map();
  (rows || []).forEach((row) => {
    const lineFinancements = getDestinationFinancements(row, transport);
    lineFinancements.forEach((financement, index) => {
      const key = buildFinancementKey(financement, index);
      if (!mapByKey.has(key)) {
        mapByKey.set(key, financement);
      }
    });
  });
  return Array.from(mapByKey.values());
};

const useStyles = makeStyles((theme) => ({
 paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    borderRadius: "8px",
    marginTop: "2%",
  },
 fieldset: {
    boxSizing: "border-box",
     borderRadius: "10px",
    color: "#1976d2",
    fontSize: "20px",
    marginTop: "10px",
    marginBottom: "16px",
    padding: "10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    border: "1px solid #d7e1f3",
    backgroundColor: "#ffffff",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
    "& legend": {
      padding: "0 10px",
      color: "#2f5ca3",
      fontWeight: 700,
      letterSpacing: "0.2px",
    },
  },
 tableWrapper: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
  },
 recapTable: {
    minWidth: 900,
  },
 TABLE_SX :{
  "& .MuiTableCell-root": {
    py: 1.2,
    px: 1.5,
    verticalAlign: "top",
    fontSize: "0.875rem",
  },
  "& .MuiTableCell-alignRight": {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: "grey.100",
    color: "text.secondary",
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
    backgroundColor: "action.hover",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "action.selected",
  },
  "& .MuiTableFooter-root .MuiTableCell-root": {
    borderTop: "2px solid",
    borderTopColor: "divider",
    backgroundColor: "grey.50",
    fontWeight: 700,
  }},

  FINANCEMENT_CELL_SX : {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.25,
  px: 0.75,
  py: 0.5,
      border: "1px solid #d7e1f3",
  backgroundColor: "grey.50",
  borderRadius: "8px",
  lineHeight: 1.2,
},
  feedbackDialogContent: {
    padding: theme.spacing(4),
    textAlign: "center",
  },
}));

const AccountingFuelValidate = (props) => {
  const { mission_planning_id, timesheet_id, agenda_id, provisional_fuel_id } = props;
  const navigate = useNavigate();
  const classes = useStyles();
  const [mission, setMission] = useState(null);
  const [loadingBackdrop, setLoadingBackdrop] = useState(false);
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
  const downloadInProgressRef = useRef(false);
  const [isLoadingSkeleton, setIsLoadingSkeleton] = useState(false);
  const [transports, setTransports] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [tabValue, setTabValue] = useState("recap");
  const [provisionalFuel, setProvisionalFuel] = useState(null);
  const [refusDialog, setRefusDialog] = useState({
    open: false,
    observation : "",
  });
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [error, setError] = useState({open: false, message: ""});
  const [success, setSuccess] = useState({open: false, message: ""});

  const handleCloseValidationDialog = () => {
    const validationSucceeded = validationDialog.type === "success";
    setValidationDialog((prev) => ({ ...prev, open: false }));
    // if (validationSucceeded) {
    //   navigate("/accounting/fuel_confirmed");
    // }
  };

  const fetchMission = useCallback(async () => {
    const response = await MissionSummaryService.getMissionData(
      mission_planning_id,
      agenda_id
    );
    if (response && response.status) {
      setMission(response.data);
      return;
    }
    setMission(null);
  }, [mission_planning_id, agenda_id]);

  const fetchTransportsMissions = useCallback(async (missionId, agendaId) => {
    const response = await MissionTransportService.transportsByMissionPlanning(
      missionId,
      agendaId
    );
    const list = response && response.status ? response.data || [] : [];
    setTransports(list);
    return list;
  }, []);

  
  const fetchDestinationsForTransports = useCallback(async (transportList) => {
    if (!transportList.length) {
      setDestinations([]);
      return;
    }

    setIsLoadingSkeleton(true);
    try {
      const hydrated = await Promise.all(
        transportList.map(async (transport, index) => {
          if (transport?.transport_id === undefined || transport?.transport_id === null) {
            console.warn(
              `[AccountingFuelValidate] transport_id missing at index ${index}`,
              transport
            );
            return null;
          }
          const result =
            await ProvisionalFuelMissionService.fuelProvisionnalByTransport(
              transport.transport_id,
              transport.mission_rent_id || null,
              provisional_fuel_id
            );
          return {
            transport_id: transport.transport_id,
            destinations:
              result && result.status && Array.isArray(result.data)
                ? result.data
                : [],
          };
        })
      );
      setDestinations(hydrated.filter(Boolean));
    } finally {
      setIsLoadingSkeleton(false);
    }
  }, [provisional_fuel_id]);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      if (!mission_planning_id || !agenda_id) {
        setMission(null);
        setTransports([]);
        setDestinations([]);
        return;
      }
      setLoadingBackdrop(true);
      setDestinations([]);
      try {
        await fetchMission();
        const list = await fetchTransportsMissions(mission_planning_id, agenda_id);
        if (active) {
          await fetchDestinationsForTransports(list);
        }
      } catch (error) {
        console.error("Error loading accounting fuel data:", error);
      } finally {
        if (active) {
          setLoadingBackdrop(false);
        }
      }
    };

    loadData();
    return () => {
      active = false;
    };
  }, [
    mission_planning_id,
    agenda_id,
    timesheet_id,
    fetchMission,
    fetchTransportsMissions,
    fetchDestinationsForTransports,
  ]);

  const validTransports = useMemo(
    () =>
      transports.filter(
        (transport) =>
          transport?.transport_id !== undefined && transport?.transport_id !== null
      ),
    [transports]
  );

  const destinationsByTransportId = useMemo(() => {
    const map = {};
    destinations.forEach((entry) => {
      map[entry.transport_id] = entry.destinations || [];
    });
    return map;
  }, [destinations]);

  const recapFinancementColumns = useMemo(() => {
    const columnsByKey = new Map();
    validTransports.forEach((transport) => {
      const rows = destinationsByTransportId[transport.transport_id] || [];
      getTransportFinancementColumns(transport, rows).forEach(
        (financement, index) => {
          const key = buildFinancementKey(financement, index);
          if (!columnsByKey.has(key)) {
            columnsByKey.set(key, financement);
          }
        }
      );
    });
    return Array.from(columnsByKey.values());
  }, [validTransports, destinationsByTransportId]);

  const vehicleRecapRows = useMemo(
    () =>
      validTransports.map((transport) => {
        const rows = destinationsByTransportId[transport.transport_id] || [];
        const kmTotal = rows.reduce((sum, row) => sum + parseNumber(row?.distance_km), 0);
        const consoTotale = rows.reduce(
          (sum, row) =>
            sum + parseNumber(row?.mission_timesheet_details_consumption),
          0
        );
        const financementTotals = {};
        recapFinancementColumns.forEach((financement, index) => {
          financementTotals[buildFinancementKey(financement, index)] = 0;
        });
        rows.forEach((row) => {
          const baseAmount = parseNumber(row?.mission_timesheet_details_amount, 0);
          getDestinationFinancements(row, transport).forEach(
            (financement, index) => {
              const key = buildFinancementKey(financement, index);
              financementTotals[key] =
                (financementTotals[key] || 0) +
                getFinancementAmount(financement, baseAmount);
            }
          );
        });
        const montantTotal = Object.values(financementTotals).reduce(
          (sum, amount) => sum + parseNumber(amount, 0),
          0
        );
        return {
          transportId: transport.transport_id,
          vehicleLabel: getVehicleLabel(transport),
          transportType: getTransportType(transport),
          transportMode: getTransportMode(transport),
          driverName: transport?.driver_name || "-",
          captainName: transport?.captain_name || "-",
          nbTrajets: rows.length,
          kmTotal,
          consoTotale,
          financementTotals,
          montantTotal,
          prixMoyenLitre: consoTotale > 0 ? montantTotal / consoTotale : 0,
        };
      }),
    [validTransports, destinationsByTransportId, recapFinancementColumns]
  );

  const transportMeansRecapRows = useMemo(() => {
    const grouped = new Map();
    vehicleRecapRows.forEach((row) => {
      const key = `${row.transportType}__${row.transportMode}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          transportType: row.transportType,
          transportMode: row.transportMode,
          transportIds: new Set(),
          nbTrajets: 0,
          kmTotal: 0,
          consoTotale: 0,
          montantTotal: 0,
        });
      }
      const current = grouped.get(key);
      current.transportIds.add(row.transportId);
      current.nbTrajets += row.nbTrajets;
      current.kmTotal += row.kmTotal;
      current.consoTotale += row.consoTotale;
      current.montantTotal += row.montantTotal;
    });

    return Array.from(grouped.values())
      .map((row) => ({
        transportType: row.transportType,
        transportMode: row.transportMode,
        nbVoitures: row.transportIds.size,
        nbTrajets: row.nbTrajets,
        kmTotal: row.kmTotal,
        consoTotale: row.consoTotale,
        montantTotal: row.montantTotal,
        prixMoyenLitre: row.consoTotale > 0 ? row.montantTotal / row.consoTotale : 0,
      }))
      .sort((a, b) => b.montantTotal - a.montantTotal);
  }, [vehicleRecapRows]);

  const globalTotals = useMemo(
    () =>
      vehicleRecapRows.reduce(
        (acc, row) => ({
          nbVoitures: acc.nbVoitures + 1,
          nbTrajets: acc.nbTrajets + row.nbTrajets,
          kmTotal: acc.kmTotal + row.kmTotal,
          consoTotale: acc.consoTotale + row.consoTotale,
          montantTotal: acc.montantTotal + row.montantTotal,
        }),
        {
          nbVoitures: 0,
          nbTrajets: 0,
          kmTotal: 0,
          consoTotale: 0,
          montantTotal: 0,
        }
      ),
    [vehicleRecapRows]
  );

  const globalFinancementTotals = useMemo(() => {
    const totals = {};
    recapFinancementColumns.forEach((financement, index) => {
      const key = buildFinancementKey(financement, index);
      totals[key] = vehicleRecapRows.reduce(
        (sum, row) => sum + parseNumber(row.financementTotals?.[key], 0),
        0
      );
    });
    return totals;
  }, [recapFinancementColumns, vehicleRecapRows]);

  const hasAnyDestination = useMemo(
    () => destinations.some((entry) => (entry.destinations || []).length > 0),
    [destinations]
  );

  useEffect(() => {
    if (tabValue === "recap") {
      return;
    }
    const exists = validTransports.some(
      (transport) => `transport-${transport.transport_id}` === tabValue
    );
    if (!exists) {
      setTabValue("recap");
    }
  }, [tabValue, validTransports]);

  useEffect(() => {
    if(provisional_fuel_id){
      getProvisional();
    }
  }, [provisional_fuel_id]);

  const selectedTransport = useMemo(
    () =>
      validTransports.find(
        (transport) => `transport-${transport.transport_id}` === tabValue
      ) || null,
    [validTransports, tabValue]
  );

  const selectedDestinations = useMemo(
    () =>
      selectedTransport
        ? destinationsByTransportId[selectedTransport.transport_id] || []
        : [],
    [selectedTransport, destinationsByTransportId]
  );

  const selectedFinancementColumns = useMemo(
    () =>
      selectedTransport
        ? getTransportFinancementColumns(selectedTransport, selectedDestinations)
        : [],
    [selectedTransport, selectedDestinations]
  );

  const getProvisional = async () => {
    try {
      const provisionfuel = await ProvisionalFuelMissionService.getProvisionalFuelMission(provisional_fuel_id);
      if(provisionfuel && provisionfuel.status){
        setProvisionalFuel(provisionfuel.data);
      } else {
        setError({open: true, message: provisionfuel?.message || "Une erreur est survenue lors de la recuperation du carburant provisionnel."});
      }
    } catch (error) {
      console.error("Error fetching provisional fuel mission:", error);
      setError({open: true, message: "Une erreur est survenue lors de la recuperation du carburant provisionnel."});
    }
  }

  const provisionalFuelRefusing = async () => {
    if (!provisional_fuel_id) {
      setError({open: true, message: "ID de carburant provisionnel manquant. Impossible de procéder au refus."});
      return;
    }

    const observation = refusDialog.observation?.trim() || "";
    if(!observation){
      setError({open: true, message: "Veuillez fournir une observation pour le refus."});
      return;
    }

    setLoadingBackdrop(true);
    try {
     const response = await ProvisionalFuelMissionService.refuseProvisionalFuelMission(provisional_fuel_id, observation, mission_planning_id, agenda_id);
      if(response && response.status){
        setProvisionalFuel((previous) => ({
          ...(previous || {}),
          provisional_fuel_id: previous?.provisional_fuel_id || provisional_fuel_id,
          provisional_fuel_state: "REFUSED",
          observation,
        }));
        setSuccess({
          open: true,
          message: response?.message || "Le refus a ete enregistre avec succes.",
        });
        setError({open: false, message: ""});
        setRefusDialog({ open: false, observation: "" });
        await getProvisional();
      } else {
        setError({open: true, message: response?.message || "Une erreur est survenue lors du refus du carburant provisionnel."});
      }

    } catch (error) {
      console.error("Error refusing provisional fuel:", error);
      setError({open: true, message: "Une erreur est survenue lors du refus du carburant provisionnel."});
    } finally {
      setLoadingBackdrop(false);
    }
  }

  const downloadValidatedFiles = async (zipMeta = {}) => {
    const fuelId = zipMeta.provisional_fuel_id || provisional_fuel_id;
    if (!fuelId || downloadInProgressRef.current) {
      return false;
    }

    downloadInProgressRef.current = true;
    setIsDownloadingFiles(true);

    let fileURL = null;
    let fileLink = null;

    try {
      const zipBlobData =
        await ProvisionalFuelMissionService.downloadValidatedProvisionalFuelZip(
          fuelId
        );
      const zipBlob = zipBlobData instanceof Blob
        ? zipBlobData
        : new Blob([zipBlobData], { type: "application/zip" });

      if (!zipBlob.size) {
        throw new Error("L'archive PDF + Excel est vide.");
      }

      const contentType = (zipBlob.type || "").toLowerCase();
      if (
        contentType.includes("application/json") ||
        contentType.startsWith("text/")
      ) {
        let message = "L'archive PDF + Excel est indisponible.";
        try {
          const payload = JSON.parse(await zipBlob.text());
          message = payload?.message || message;
        } catch (parseError) {
          // La réponse n'est pas un JSON exploitable.
        }
        throw new Error(message);
      }

      fileURL = window.URL.createObjectURL(zipBlob);
      fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute(
        "download",
        zipMeta.file_name || `ProvisionalFuel_${fuelId}.zip`
      );
      document.body.appendChild(fileLink);
      fileLink.click();

      return true;
    } catch (downloadError) {
      let message =
        downloadError?.message ||
        "Une erreur est survenue pendant le téléchargement du PDF et de l'Excel.";
      const errorBlob = downloadError?.response?.data;

      if (errorBlob instanceof Blob && errorBlob.size) {
        try {
          const payload = JSON.parse(await errorBlob.text());
          message = payload?.message || message;
        } catch (parseError) {
          // Conserver le message d'erreur déjà disponible.
        }
      }

      setValidationDialog({
        open: true,
        type: "error",
        message,
      });
      return false;
    } finally {
      if (fileLink?.parentNode) {
        fileLink.parentNode.removeChild(fileLink);
      }
      if (fileURL) {
        window.URL.revokeObjectURL(fileURL);
      }
      downloadInProgressRef.current = false;
      setIsDownloadingFiles(false);
    }
  };

  const validateProvisionalFuelMission = async () => {
    setLoadingBackdrop(true);

    try {
      const response = await ProvisionalFuelMissionService.validateProvisionalFuelMission(provisional_fuel_id, mission_planning_id, agenda_id);
      if(response && response.status){
        setValidationDialog({
          open: true,
          type: "success",
          message: response?.message || "Le carburant provisionnel a ete valide avec succes.",
        });
        const zipMeta = response?.data?.zip_file;
        if (zipMeta?.provisional_fuel_id) {
          await downloadValidatedFiles(zipMeta);
        }
        await fetchMission();
        await getProvisional();
      } else {
        setValidationDialog({
          open: true,
          type: "error",
          message: response?.message || "Une erreur est survenue lors de la validation du carburant provisionnel.",
        });
      }
    } catch (error) {
      console.error("Error validating provisional fuel:", error);
      setValidationDialog({
        open: true,
        type: "error",
        message: "Une erreur est survenue lors de la validation du carburant provisionnel.",
      });
    } finally {
      setLoadingBackdrop(false);
    }
  }

  const selectedFinancementTotals = useMemo(() => {
    if (!selectedTransport || !selectedFinancementColumns.length) {
      return {};
    }
    const totals = {};
    selectedFinancementColumns.forEach((financement, index) => {
      totals[buildFinancementKey(financement, index)] = 0;
    });
    selectedDestinations.forEach((destination) => {
      const base = parseNumber(destination?.mission_timesheet_details_amount, 0);
      const rowFinancements = getDestinationFinancements(destination, selectedTransport);
      const rowByKey = rowFinancements.reduce((acc, fin, index) => {
        acc[buildFinancementKey(fin, index)] = fin;
        return acc;
      }, {});
      selectedFinancementColumns.forEach((financement, index) => {
        const key = buildFinancementKey(financement, index);
        const fin = rowByKey[key];
        if (fin) {
          totals[key] += getFinancementAmount(fin, base);
        }
      });
    });
    return totals;
  }, [selectedTransport, selectedFinancementColumns, selectedDestinations]);

  const selectedKmTotal = selectedDestinations.reduce(
    (sum, row) => sum + parseNumber(row?.distance_km),
    0
  );
  const selectedConsoTotal = selectedDestinations.reduce(
    (sum, row) => sum + parseNumber(row?.mission_timesheet_details_consumption),
    0
  );
  const selectedAmountTotal = Object.values(selectedFinancementTotals).reduce(
    (sum, amount) => sum + parseNumber(amount, 0),
    0
  );


  return (
    <>
      <LoadingCustomBackdrop open={loadingBackdrop} />
      <Paper className={classes.paper} variant="outlined">
        <Spacer y={2} />
        <MissionData mission={mission} />
        <Box sx={{ marginTop: "16px" }}>
        {success.open && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess({ open: false, message: "" })}
          >
            {success.message}
          </Alert>
        )}
        <Box justifyContent={"flex-end"} display="flex" gap={2}>
         { provisionalFuel && provisionalFuel.provisional_fuel_state === "PENDING" && <> <Button
          variant="contained"
          color="error"
          onClick={() => {
            setError({open: false, message: ""});
            setRefusDialog({
              ...refusDialog,
              open: true
            });
          }}
          >
            Réfuser
            </Button>
            <Button
            variant="contained"
              color="success"
              onClick={validateProvisionalFuelMission}
              >
              Valider
              </Button></>}
              {
                provisionalFuel && provisionalFuel.provisional_fuel_state === "REFUSED" && <Chip label="Carburant provisionnel refusé" color="error" />
              }
              {
                provisionalFuel && provisionalFuel.provisional_fuel_state === "CONFIRMED" && <>
                  <Chip label="Carburant provisionnel validé" color="success" />
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    disabled={isDownloadingFiles}
                    onClick={() => downloadValidatedFiles()}
                  >
                    {isDownloadingFiles
                      ? "Téléchargement…"
                      : "Télécharger"}
                  </Button>
                </>
              }
        </Box>
          <Alert severity="error" size="small" sx={{ marginTop: "1%", fontSize: "12px" }}>
                <span>
                  <strong>Veuillez défiler horizontalement le tableau pour voir toutes les colonnes.</strong>
                </span>
                </Alert>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Details du carburant par vehicule"
        >
          <Tab value="recap" label="Recapitulatif" />
          {validTransports.map((transport) => (
            <Tab
              key={transport.transport_id}
              value={`transport-${transport.transport_id}`}
              label={getVehicleLabel(transport)}
              wrapped
            />
          ))}
        </Tabs>

        {tabValue === "recap" && (
          <Box sx={{ mt: 2 }}>
            {!validTransports.length && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Aucun moyen de transport associe a cette mission.
              </Alert>
            )}
            {validTransports.length > 0 && !hasAnyDestination && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Aucun detail carburant trouve. Les totaux sont initialises a 0.
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={2}>
                <Paper variant="outlined"  sx={{ p: 1.5, border: "1px solid #d7e1f3", borderRadius: "8px" }}>
                  <Typography variant="caption">Voitures</Typography>
                  <Typography variant="h6">{globalTotals.nbVoitures}</Typography>
                </Paper>
              </Grid>
             
              <Grid item xs={12} sm={6} md={2}>
                <Paper variant="outlined" sx={{ p: 1.5, border: "1px solid #d7e1f3", borderRadius: "8px" }}>
                  <Typography variant="caption">Conso totale (L)</Typography>
                  <Typography variant="h6">
                    {formatWithThousandSeparator(globalTotals.consoTotale, {
                      fallback: "0",
                    })}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Paper variant="outlined" sx={{ p: 1.5, border: "1px solid #d7e1f3", borderRadius: "8px"   }}>
                  <Typography variant="caption">Montant total (Ar)</Typography>
                  <Typography variant="h6">
                    {formatWithThousandSeparator(globalTotals.montantTotal, {
                      fallback: "0",
                    })}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

           <fieldset className={classes.fieldset}>
            <legend>Recapitulatif par voiture</legend>
            <TableContainer className={classes.tableWrapper}>
                        <Table size="small" className={classes.recapTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Voiture</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Chauffeur</TableCell>
                    <TableCell>Chef de bord</TableCell>
                    <TableCell align="right">KM</TableCell>
                    <TableCell align="right">Conso (L)</TableCell>
                    {recapFinancementColumns.map((financement, index) => (
                      <TableCell
                        key={`recap-fin-head-${buildFinancementKey(financement, index)}`}
                        align="right"
                      >
                        {financement?.code_financement_mis || "-"} (Ar)
                      </TableCell>
                    ))}
                    <TableCell align="right">Montant total (Ar)</TableCell>
                    <TableCell align="right">Prix unitaire (Ar/L)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingSkeleton &&
                    Array.from({ length: 3 }).map((_, rowIndex) => (
                      <TableRow key={`recap-skeleton-${rowIndex}`}>
                        {Array.from({ length: 8 + recapFinancementColumns.length }).map((__, cellIndex) => (
                          <TableCell key={`recap-skeleton-${rowIndex}-${cellIndex}`}>
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  {!isLoadingSkeleton &&
                    vehicleRecapRows.map((row) => (
                      <TableRow key={`vehicle-${row.transportId}`} onClick={() => setTabValue(`transport-${row.transportId}`)} sx={{ cursor: "pointer" }}>
                        <TableCell>{row.vehicleLabel}</TableCell>
                        <TableCell>{row.transportType}</TableCell>
                        <TableCell>{row.driverName}</TableCell>
                        <TableCell>{row.captainName}</TableCell>
                        <TableCell align="right">
                          {formatWithThousandSeparator(row.kmTotal, { fallback: "0" })}
                        </TableCell>
                        <TableCell align="right">
                          {formatWithThousandSeparator(row.consoTotale, { fallback: "0" })}
                        </TableCell>
                        {recapFinancementColumns.map((financement, index) => {
                          const key = buildFinancementKey(financement, index);
                          return (
                            <TableCell key={`vehicle-fin-${row.transportId}-${key}`} align="right">
                              {formatWithThousandSeparator(
                                row.financementTotals?.[key] || 0,
                                { fallback: "0" }
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          {formatWithThousandSeparator(row.montantTotal, { fallback: "0" })}
                        </TableCell>
                        <TableCell align="right">
                          {formatWithThousandSeparator(row.prixMoyenLitre, {
                            fallback: "0",
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: "bold" }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(globalTotals.kmTotal, { fallback: "0" })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(globalTotals.consoTotale, { fallback: "0" })}
                    </TableCell>
                    {recapFinancementColumns.map((financement, index) => {
                      const key = buildFinancementKey(financement, index);
                      return (
                        <TableCell key={`global-fin-${key}`} align="right" sx={{ fontWeight: "bold" }}>
                          {formatWithThousandSeparator(globalFinancementTotals[key] || 0, {
                            fallback: "0",
                          })}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(globalTotals.montantTotal, { fallback: "0" })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(
                        globalTotals.consoTotale > 0
                          ? globalTotals.montantTotal / globalTotals.consoTotale
                          : 0,
                        { fallback: "0" }
                      )}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
            </fieldset>
          </Box>
        )}

        {tabValue !== "recap" && selectedTransport && (
          <Box sx={{ mt: 2 }}>
            <TransportData transport={selectedTransport} />
            <RentalChargeTable
              transport={selectedTransport}
              destinations={selectedDestinations}
            />
            <fieldset className={classes.fieldset}>
            <TableContainer
              className={classes.tableWrapper}
            >
              <Table size="small" sx={{ minWidth: 1400 }} className={classes.TABLE_SX}>
                {/* <TableHead> */}
                  <TableRow >
                    <TableCell sx={{width: "10px"}}>Date(s)</TableCell>
                    <TableCell>Heure depart</TableCell>
                    <TableCell>Heure arrivee</TableCell>
                    <TableCell>Lieu depart</TableCell>
                    <TableCell>Lieu arrivee</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Missionnaire(s)</TableCell>
                    <TableCell align="right">KM</TableCell>
                    <TableCell align="right">Conso (L)</TableCell>
                    <TableCell align="right">Prix (Ar/L)</TableCell>
                    {(selectedFinancementColumns.length
                      ? selectedFinancementColumns
                      : [null]
                    ).map((financement, index) => (
                      <TableCell
                        key={`fin-head-${buildFinancementKey(financement, index)}`}
                        align="center"
                      >
                        {financement?.code_financement_mis || "-"}
                      </TableCell>
                    ))}
                    <TableCell align="right">Carburant (Ar)</TableCell>
                  </TableRow>
                {/* </TableHead> */}
                <TableBody>
                  {isLoadingSkeleton &&
                    Array.from({ length: 3 }).map((_, rowIndex) => (
                      <TableRow key={`detail-skeleton-${rowIndex}`}>
                        {Array.from({ length: 11 + Math.max(selectedFinancementColumns.length, 1) }).map((__, cellIndex) => (
                          <TableCell key={`detail-skeleton-${rowIndex}-${cellIndex}`}>
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  {!isLoadingSkeleton &&
                    selectedDestinations.map((destination, rowIndex) => {
                      const amount = parseNumber(destination?.mission_timesheet_details_amount);
                      const rowFinancements = getDestinationFinancements(
                        destination,
                        selectedTransport
                      );
                      const rowByKey = rowFinancements.reduce((acc, fin, index) => {
                        acc[buildFinancementKey(fin, index)] = fin;
                        return acc;
                      }, {});
                      const rowDistributionTotal = rowFinancements.reduce(
                        (sum, financement) =>
                          sum + getFinancementAmount(financement, amount),
                        0
                      );
                      const missionnaries =
                        Array.isArray(destination?.missionnaries) &&
                        destination.missionnaries.length
                          ? destination.missionnaries
                          : Array.isArray(selectedTransport?.travelers)
                          ? selectedTransport.travelers
                          : [];

                      return (
                        <TableRow key={`destination-${rowIndex}`}>
                          <TableCell  sx={{width: "10px"}}>
                            {formatDate(destination?.date_begin) ===
                            formatDate(destination?.date_end)
                              ? formatDate(destination?.date_begin)
                              : `${formatDate(destination?.date_begin)} - ${formatDate(
                                  destination?.date_end
                                )}`}
                          </TableCell>
                          <TableCell>{destination?.heure_begin || "-"}</TableCell>
                          <TableCell>{destination?.heure_end || "-"}</TableCell>
                          <TableCell sx={{fontSize: "10px", color: "gray"}}>{getLocationLabel(destination, "depart")}</TableCell>
                          <TableCell sx={{fontSize: "10px", color: "gray"}}>{getLocationLabel(destination, "arrivee")}</TableCell>
                          <TableCell>
                            {destination?.type === "DM" ? (
                              <Chip
                                icon={<CardTravelIcon />}
                                label="Mission"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                icon={<TimeToLeaveIcon />}
                                label="Delai de route"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {missionnaries.length ? (
                              <Box
                                component="ul"
                                sx={{ m: 0, pl: 2, "& li": { mb: 0.25 } }}
                              >
                                {missionnaries.map((missionary, index) => (
                                  <li key={`missionary-${rowIndex}-${index}`}>
                                    {missionary?.name || "-"}
                                  </li>
                                ))}
                              </Box>
                            ) : (
                              "Aucun missionnaire"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatWithThousandSeparator(destination?.distance_km || 0, {
                              fallback: "0",
                            })}
                          </TableCell>
                          <TableCell align="right">
                            {formatWithThousandSeparator(
                              destination?.mission_timesheet_details_consumption || 0,
                              { fallback: "0" }
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatWithThousandSeparator(
                              selectedTransport?.price_per_liter || 0,
                              { fallback: "0" }
                            )}
                          </TableCell>
                          {(selectedFinancementColumns.length
                            ? selectedFinancementColumns
                            : [null]
                          ).map((financement, index) => {
                            if (!financement) {
                              return (
                                <TableCell key={`fin-empty-${rowIndex}-${index}`} align="center">
                                  -
                                </TableCell>
                              );
                            }
                            const key = buildFinancementKey(financement, index);
                            const fin = rowByKey[key];
                            if (!fin) {
                              return (
                                <TableCell key={`fin-none-${key}-${rowIndex}`} align="center">
                                  -
                                </TableCell>
                              );
                            }
                            const percent = parseNumber(fin?.percentage_rate, 0);
                            const distributedAmount = getFinancementAmount(fin, amount);
                            return (
                              <TableCell key={`fin-${key}-${rowIndex}`} align="center">
                                <Box className={classes.FINANCEMENT_CELL_SX}>
                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                    {formatPercent(percent)}%
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.68rem",
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    {formatWithThousandSeparator(distributedAmount, {
                                      fallback: "0",
                                    })}{" "}
                                    Ar
                                  </Typography>
                                </Box>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            {formatWithThousandSeparator(rowDistributionTotal, {
                              fallback: "0",
                            })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {!isLoadingSkeleton && selectedDestinations.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={11 + Math.max(selectedFinancementColumns.length, 1)}
                        align="center"
                      >
                        Aucun trajet pour cette voiture
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: "bold" }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(selectedKmTotal, { fallback: "0" })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(selectedConsoTotal, { fallback: "0" })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(
                        selectedTransport?.price_per_liter || 0,
                        { fallback: "0" }
                      )}
                    </TableCell>
                    {(selectedFinancementColumns.length
                      ? selectedFinancementColumns
                      : [null]
                    ).map((financement, index) => {
                      if (!financement) {
                        return <TableCell key={`fin-total-empty-${index}`}>-</TableCell>;
                      }
                      const key = buildFinancementKey(financement, index);
                      return (
                        <TableCell key={`fin-total-${key}`} align="center" sx={{ fontWeight: "bold" }}>
                          {formatWithThousandSeparator(selectedFinancementTotals[key] || 0, {
                            fallback: "0",
                          })}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatWithThousandSeparator(selectedAmountTotal, { fallback: "0" })}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
            </fieldset>
          </Box>
        )}
        </Box>
      </Paper>

      <Dialog
        open={validationDialog.open}
        onClose={handleCloseValidationDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="fuel-validation-feedback-title"
      >
        <DialogTitle
          id="fuel-validation-feedback-title"
          sx={{
            backgroundColor: validationDialog.type === "success" ? "#4caf50" : "#f44336",
            color: "#ffffff",
            textAlign: "center",
          }}
        >
          {validationDialog.type === "success" ? "Succès !" : "Erreur"}
        </DialogTitle>
        <DialogContent className={classes.feedbackDialogContent}>
          {validationDialog.type === "error" && (
            <ErrorOutlineIcon sx={{ fontSize: 64, color: "#f44336", mb: 1 }} />
          )}
          <Typography
            variant="h6"
            sx={{ color: validationDialog.type === "success" ? "#2e7d32" : "#c62828" }}
          >
            {validationDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseValidationDialog} color="primary">
            {"Fermer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={refusDialog.open}
        onClose={() => {
          setRefusDialog({ open: false, observation: "" });
          setError({open: false, message: ""});
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="form-dialog-title">Réfuser les carburants prévisionnel</DialogTitle>
        <DialogContentText>
        <DialogContent>
          <TextField
            label="Observation"
            multiline
            rows={4}
            fullWidth
            value={refusDialog.observation}
            onChange={(e) => {
              setRefusDialog((prev) => ({ ...prev, observation: e.target.value }));
              if (error.open) {
                setError({open: false, message: ""});
              }
            }}
          />
        </DialogContent>
        <FormHelperText
        error
        >
          { error.open && error.message}
        </FormHelperText>
        </DialogContentText>
        <DialogActions>
          <Button
            onClick={() => {
              setRefusDialog({ open: false, observation: "" });
              setError({open: false, message: ""});
            }}
          >
            Annuler
          </Button>
          <Button variant="contained" color="error" onClick={provisionalFuelRefusing}>
            Confirmer le refus
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountingFuelValidate;
